import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Anchor,
  Box,
  Button,
  Container,
  CopyButton,
  Divider,
  Group,
  Image,
  Paper,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import {
  IconAt,
  IconBolt,
  IconCheck,
  IconCopy,
  IconLink,
  IconSearch,
} from "@tabler/icons-react";
import CheckAddressBackground from "../../checkBitcoinAddress/components/CheckAddressBackground";
import RizfulPitch from "../../rizfulPitch/RizfulPitch";
import ServerRenderedBlock, {
  hasServerRenderedBlock,
} from "../../helpers/ServerRenderedBlock";
import { fetchServerDecode } from "../../decodeInvoice/helpers/invoiceSummary";
import {
  Check,
  InvoiceResult,
  ParsedAddress,
  ResolveResult,
  msatToSatsText,
  parseLightningAddress,
  requestTestInvoice,
  resolveLightningAddress,
} from "../helpers/lnurlPayTest";
import { quotaMessage, requestToolQuota } from "../helpers/toolQuota";
import { trackGoal } from "../../helpers/trackGoal";
import CheckList, { STATUS_COLORS } from "./CheckList";
import TestInvoiceDisplay from "./TestInvoiceDisplay";
import ToolLogo from "../../helpers/ToolLogo";

const PANEL_STYLE = {
  backgroundColor: "rgba(0, 0, 0, 0.35)",
  border: "1px solid rgba(255, 255, 255, 0.1)",
};

/** 1,000 sats, or the nearest amount the address accepts; null when it accepts no whole number of sats. */
const testAmountSats = (minMsat: number, maxMsat: number): number | null => {
  const min = Math.max(1, Math.ceil(minMsat / 1000));
  const max = Math.floor(maxMsat / 1000);
  return min <= max ? Math.min(max, Math.max(min, 1000)) : null;
};

const shareUrl = (address: string) =>
  `${window.location.origin}/lightning-address?address=${encodeURIComponent(address)}`;

/** The receiving node, from our own Lightning graph (POST /api/decode_invoice). */
// one word for Plausible: "fail" if any check failed, else "warn" if any warned, else "pass"
const worstStatus = (checks: Check[]): string =>
  checks.some((c) => c.status === "fail") ? "fail" : checks.some((c) => c.status === "warn") ? "warn" : "pass";

async function payeeNodeCheck(pr: string): Promise<Check | null> {
  try {
    const server = await fetchServerDecode(pr);
    if (!server.destination) return null;
    const short = `${server.destination.slice(0, 10)}…${server.destination.slice(-6)}`;
    if (server.destination_alias) {
      return {
        status: "info",
        label: `Receiving node: ${server.destination_alias}`,
        detail: short,
      };
    }
    return {
      status: "info",
      label: "The receiving node isn't in the public Lightning graph",
      detail: `${short}. It's probably a private node reached through route hints, which is normal for many wallets.`,
    };
  } catch {
    return null;
  }
}

const Verdict: React.FC<{
  resolve: ResolveResult;
  invoice: InvoiceResult | null;
}> = ({ resolve, invoice }) => {
  const checks = [...resolve.checks, ...(invoice?.checks ?? [])];
  const fails = checks.filter((c) => c.status === "fail").length;
  const warns = checks.filter((c) => c.status === "warn").length;
  const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

  let color = STATUS_COLORS.pass;
  let text: string;
  if (fails > 0) {
    color = STATUS_COLORS.fail;
    text = resolve.params
      ? `Found ${plural(fails, "problem")}. Some wallets won't be able to pay this address.`
      : `Found ${plural(fails, "problem")}. Wallets can't pay this address right now.`;
  } else if (!invoice?.invoice) {
    text = warns
      ? `The lookup works, with ${plural(warns, "warning")}. Get a test invoice to check the rest.`
      : "The lookup works. Get a test invoice to check the rest.";
  } else if (warns) {
    color = STATUS_COLORS.warn;
    text = `This Lightning Address works, with ${plural(warns, "warning")}.`;
  } else {
    text = "This Lightning Address works and passes every check.";
  }

  return (
    <Text fw={700} size="md" style={{ color, lineHeight: 1.45 }}>
      {text}
    </Text>
  );
};

const LightningAddressContainer: React.FC = () => {
  const [input, setInput] = useState("");
  const [tested, setTested] = useState<ParsedAddress | null>(null);
  const [inputError, setInputError] = useState<string | null>(null);
  const [resolving, setResolving] = useState(false);
  const [resolve, setResolve] = useState<ResolveResult | null>(null);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [invoice, setInvoice] = useState<InvoiceResult | null>(null);
  const [invoiceError, setInvoiceError] = useState<string | null>(null);
  // drops results that arrive after the visitor started another test
  const runId = useRef(0);

  const reset = () => {
    runId.current += 1;
    setTested(null);
    setInputError(null);
    setResolve(null);
    setResolving(false);
    setInvoice(null);
    setInvoiceError(null);
    setInvoiceLoading(false);
  };

  const runTest = async (raw: string) => {
    reset();
    const parsed = parseLightningAddress(raw);
    if (typeof parsed === "string") {
      setInputError(parsed);
      return;
    }
    const run = runId.current;
    setTested(parsed);
    window.history.replaceState(null, "", shareUrl(parsed.address));
    setResolving(true);

    const quota = await requestToolQuota("resolve", parsed.address);
    if (run !== runId.current) return;
    if (quota !== "ok") {
      setInputError(quotaMessage("resolve", quota));
      setResolving(false);
      trackGoal("Lightning Address tested", { step: "resolve", result: "limited" });
      return;
    }

    const result = await resolveLightningAddress(parsed);
    if (run !== runId.current) return;
    setResolve(result);
    setResolving(false);
    trackGoal("Lightning Address tested", { step: "resolve", result: worstStatus(result.checks) });
  };

  const getInvoice = async () => {
    if (!tested || !resolve?.params) return;
    const sats = testAmountSats(resolve.params.minSendable, resolve.params.maxSendable);
    if (!sats) return;
    const run = runId.current;
    setInvoiceLoading(true);
    setInvoiceError(null);
    setInvoice(null);

    const quota = await requestToolQuota("invoice", tested.address);
    if (run !== runId.current) return;
    if (quota !== "ok") {
      setInvoiceError(quotaMessage("invoice", quota));
      setInvoiceLoading(false);
      trackGoal("Lightning Address tested", { step: "invoice", result: "limited" });
      return;
    }

    const result = await requestTestInvoice(resolve.params, sats * 1000);
    if (run !== runId.current) return;
    setInvoice(result);
    setInvoiceLoading(false);
    trackGoal("Lightning Address tested", { step: "invoice", result: worstStatus(result.checks) });

    if (result.invoice) {
      const node = await payeeNodeCheck(result.invoice.pr);
      if (node && run === runId.current) {
        setInvoice({ ...result, checks: [...result.checks, node] });
      }
    }
  };

  useEffect(() => {
    const param = new URLSearchParams(window.location.search).get("address");
    if (param) {
      setInput(param);
      runTest(param);
    }
  }, []);

  const clearAll = () => {
    setInput("");
    reset();
    window.history.replaceState(null, "", window.location.pathname);
  };

  const params = resolve?.params ?? null;
  const testSats = params ? testAmountSats(params.minSendable, params.maxSendable) : null;

  return (
    <CheckAddressBackground>
      <Container size={700} px="md" py="xl">
        <Stack gap="lg">
          <ToolLogo />

          {hasServerRenderedBlock("ssr-lnaddr-head") ? (
            <ServerRenderedBlock id="ssr-lnaddr-head" />
          ) : (
            <Box ta="center">
              <Title order={1} c="white" mb="xs">
                Lightning Address Tester
              </Title>
              <Text c="dimmed" size="sm">
                Check whether a Lightning Address (user@domain) can really
                receive payments.
              </Text>
            </Box>
          )}

          <Paper p="md" radius="md" style={PANEL_STYLE}>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                runTest(input);
              }}
            >
              <Stack gap="sm">
                <TextInput
                  aria-label="Lightning Address"
                  placeholder="name@example.com"
                  value={input}
                  onChange={(e) => {
                    setInput(e.currentTarget.value);
                    if (tested || inputError) reset();
                  }}
                  leftSection={<IconAt size={16} />}
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  inputMode="email"
                  size="md"
                  error={inputError}
                  styles={{
                    input: {
                      fontFamily: "monospace",
                      backgroundColor: "rgba(0, 0, 0, 0.3)",
                      border: "1px solid rgba(255, 255, 255, 0.15)",
                      color: "white",
                    },
                  }}
                />
                <Group justify="space-between" gap="xs">
                  <Text size="xs" c="dimmed" style={{ flex: 1, minWidth: 180 }}>
                    Free. Nothing gets paid: a test invoice is only shown, never paid.
                  </Text>
                  <Group gap="xs">
                    <Button
                      variant="subtle"
                      color="gray"
                      onClick={clearAll}
                      disabled={!input}
                    >
                      Clear
                    </Button>
                    <Button
                      type="submit"
                      leftSection={<IconSearch size={16} />}
                      loading={resolving}
                      disabled={!input.trim()}
                    >
                      Test address
                    </Button>
                  </Group>
                </Group>
              </Stack>
            </form>
          </Paper>

          {resolve && tested && (
            <Paper p="md" radius="md" style={PANEL_STYLE}>
              <Stack gap="md">
                <Group gap="sm" wrap="nowrap" align="center">
                  {params?.thumbnail && (
                    <Image
                      src={params.thumbnail}
                      alt=""
                      w={44}
                      h={44}
                      radius="md"
                      style={{ flexShrink: 0 }}
                    />
                  )}
                  <Box style={{ minWidth: 0 }}>
                    <Text
                      c="white"
                      fw={700}
                      style={{ fontFamily: "monospace", overflowWrap: "anywhere" }}
                    >
                      {tested.address}
                    </Text>
                    {params?.description && (
                      <Text size="xs" c="dimmed" lineClamp={2}>
                        {params.description}
                      </Text>
                    )}
                  </Box>
                </Group>

                <Verdict resolve={resolve} invoice={invoice} />

                <Divider color="rgba(255,255,255,0.08)" label="Lookup" labelPosition="left" />
                <CheckList checks={resolve.checks} />
                <Text size="xs" c="dimmed" style={{ overflowWrap: "anywhere" }}>
                  Fetched <Anchor href={resolve.url} target="_blank" rel="noopener nofollow" size="xs" c="dimmed" underline="always">{resolve.url}</Anchor>
                </Text>

                {params && (
                  <>
                    <Divider color="rgba(255,255,255,0.08)" label="Test invoice" labelPosition="left" />
                    {!invoice?.invoice && (
                      <Stack gap="sm">
                        {testSats ? (
                          <Group justify="space-between" gap="sm">
                            <Text size="sm" c="dimmed" style={{ flex: 1, minWidth: 200 }}>
                              Ask this address for a real{" "}
                              {testSats.toLocaleString()}-sat invoice to finish
                              the test. You won't pay anything.
                            </Text>
                            <Button
                              color="orange"
                              leftSection={<IconBolt size={16} />}
                              onClick={getInvoice}
                              loading={invoiceLoading}
                            >
                              Get a test invoice
                            </Button>
                          </Group>
                        ) : (
                          <Text size="sm" c="dimmed">
                            Its limits ({msatToSatsText(params.minSendable)} to{" "}
                            {msatToSatsText(params.maxSendable)}) don't include a
                            whole number of sats, so there's no amount to test with.
                          </Text>
                        )}
                        {invoiceError && (
                          <Alert color="yellow" variant="light" p="sm">
                            <Text size="sm">{invoiceError}</Text>
                          </Alert>
                        )}
                      </Stack>
                    )}
                    {invoice && <CheckList checks={invoice.checks} />}
                    {invoice?.invoice && <TestInvoiceDisplay invoice={invoice.invoice} />}
                  </>
                )}
              </Stack>
            </Paper>
          )}

          {resolve && tested && (
            <Paper p="md" radius="md" style={PANEL_STYLE}>
              <Group justify="space-between" align="center" gap="xs">
                <Group gap="xs">
                  <IconLink size={18} color="teal" />
                  <Text size="sm" c="dimmed">
                    Share this test:
                  </Text>
                </Group>
                <CopyButton value={shareUrl(tested.address)}>
                  {({ copied, copy }) => (
                    <Button
                      variant={copied ? "filled" : "outline"}
                      color={copied ? "teal" : "gray"}
                      size="xs"
                      leftSection={copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
                      onClick={copy}
                    >
                      {copied ? "Link Copied!" : "Copy Link"}
                    </Button>
                  )}
                </CopyButton>
              </Group>
              <Text size="xs" c="dimmed" mt="xs">
                Something failing on an address you don't run? Send this link to
                its provider: each failed check says what to fix.{" "}
                <Anchor href="/create-account" size="xs" c="orange.4">
                  Or get a free Lightning Address at Rizful that passes every check.
                </Anchor>
              </Text>
            </Paper>
          )}

          {hasServerRenderedBlock("ssr-lnaddr-guide") && (
            <ServerRenderedBlock id="ssr-lnaddr-guide" />
          )}

          <RizfulPitch
            lead="Want a Lightning Address that passes every check on this page?"
          />
          <ServerRenderedBlock id="ssr-tool-footer" />
        </Stack>
      </Container>
    </CheckAddressBackground>
  );
};

export default LightningAddressContainer;
